import { CalendarClock, Pause, Play, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, fmtDate, fmtKr } from "./shared";

const STATUS = {
  aktiv: ["Aktiv", "bg-[#22c55e]/10 text-[#22c55e]"],
  pauset: ["Pauset", "bg-amber-400/10 text-amber-300"],
  kjorer: ["Sender…", "bg-[#D4AF37]/15 text-[#E5C564]"],
  fullfort: ["Fullført", "bg-white/5 text-[#A8A29E]"],
};

export function ScheduledCampaigns({ items, onChange }) {
  const setStatus = async (s, status) => {
    try {
      await api.patch(`/admin/scheduled-campaigns/${s.id}`, { status });
      toast.success(status === "pauset" ? "Kampanjen er satt på pause" : "Kampanjen er aktiv igjen");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const remove = async (s) => {
    if (!window.confirm(`Slette den planlagte kampanjen «${s.navn}»?`)) return;
    try {
      await api.delete(`/admin/scheduled-campaigns/${s.id}`);
      toast.success("Planlagt kampanje slettet");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Card className="mt-10" title="Planlagte kampanjer" icon={CalendarClock} testid="scheduled-list">
      {items.length === 0 ? (
        <p className="text-sm text-[#A8A29E]" data-testid="scheduled-empty">Ingen planlagte kampanjer ennå.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-[#A8A29E]">
                <th className="px-3 py-3">Kampanje</th>
                <th className="px-3 py-3">Mottakere</th>
                <th className="px-3 py-3">Gjentakelse</th>
                <th className="px-3 py-3">Neste utsending</th>
                <th className="px-3 py-3">Slutt</th>
                <th className="px-3 py-3 text-center">Sendt</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const [label, cls] = STATUS[s.status] || STATUS.aktiv;
                return (
                  <tr key={s.id} className="border-b border-white/5" data-testid={`scheduled-row-${s.id}`}>
                    <td className="px-3 py-3">
                      <p className="font-medium text-[#FDFBF7]">{s.navn}</p>
                      <p className="text-xs text-[#A8A29E]">{s.tittel}{s.verdi_kr ? ` · ${fmtKr(s.verdi_kr)}` : ""}</p>
                    </td>
                    <td className="px-3 py-3 text-xs text-[#A8A29E]">
                      {s.gruppe_navn}{s.aldersgruppe ? ` · ${s.aldersgruppe}` : ""}
                    </td>
                    <td className="px-3 py-3 text-[#FDFBF7]">{s.gjentakelse_navn}</td>
                    <td className="px-3 py-3 text-xs text-[#E5C564]" data-testid={`next-run-${s.id}`}>{s.neste_kjoring ? fmtDate(s.neste_kjoring) : "—"}</td>
                    <td className="px-3 py-3 text-xs text-[#A8A29E]">{fmtDate(s.slutt)}</td>
                    <td className="px-3 py-3 text-center text-[#FDFBF7]" data-testid={`runs-${s.id}`}>{s.antall_kjoringer}</td>
                    <td className="px-3 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-medium ${cls}`} data-testid={`scheduled-status-${s.id}`}>{label}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-1">
                        {s.status === "aktiv" && (
                          <button onClick={() => setStatus(s, "pauset")} title="Pause" data-testid={`pause-${s.id}`} className="rounded-lg p-2 text-[#A8A29E] hover:bg-white/5 hover:text-[#D4AF37]"><Pause className="h-4 w-4" /></button>
                        )}
                        {s.status === "pauset" && (
                          <button onClick={() => setStatus(s, "aktiv")} title="Fortsett" data-testid={`resume-${s.id}`} className="rounded-lg p-2 text-[#A8A29E] hover:bg-white/5 hover:text-[#22c55e]"><Play className="h-4 w-4" /></button>
                        )}
                        {s.status === "fullfort" && <CheckCircle2 className="m-2 h-4 w-4 text-[#A8A29E]" />}
                        <button onClick={() => remove(s)} title="Slett" data-testid={`delete-${s.id}`} className="rounded-lg p-2 text-[#A8A29E] hover:bg-white/5 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
